import type { LinkTreeTheme, BlockType } from './types';

export type TemplateCategory = 'luxury' | 'artist' | 'bold' | 'minimal';

export interface Template {
  id: string;
  name: string;
  vibe: string;
  preview: string; // emoji or icon char
  category: TemplateCategory;
  theme: LinkTreeTheme;
}

export const TEMPLATES: Template[] = [
  // ===== LUXURY (NEW) =====
  {
    id: 'platinum-noir',
    name: 'Platinum Noir',
    vibe: 'Deep black with platinum',
    preview: '◆',
    category: 'luxury',
    theme: {
      templateId: 'platinum-noir',
      bg: 'radial-gradient(ellipse at top, #15151a 0%, #050507 70%)',
      text: '#F5F5F7', primary: '#E5E4E2', secondary: '#9CA3AF', accent: '#D4D4D8',
      font: 'display', buttonStyle: 'glass', buttonRadius: 14, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.04)', cardBorder: 'rgba(229,228,226,0.18)',
      mutedText: 'rgba(245,245,247,0.6)', dividerColor: 'rgba(229,228,226,0.2)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.7) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(229,228,226,0.06), transparent)',
      linkIconColor: '#E5E4E2',
    },
  },
  {
    id: 'champagne-gold',
    name: 'Champagne Gold',
    vibe: 'Warm cream & polished gold',
    preview: '🥂',
    category: 'luxury',
    theme: {
      templateId: 'champagne-gold',
      bg: 'linear-gradient(160deg, #1c1410 0%, #2a1d12 60%, #3a2818 100%)',
      text: '#FBF3DE', primary: '#E8B96A', secondary: '#C99354', accent: '#F5D58A',
      font: 'serif', buttonStyle: 'soft', buttonRadius: 24, buttonShadow: 'soft',
      cardBg: 'rgba(232,185,106,0.08)', cardBorder: 'rgba(232,185,106,0.28)',
      mutedText: 'rgba(251,243,222,0.65)', dividerColor: 'rgba(232,185,106,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(20,12,6,0) 35%, rgba(20,12,6,0.7) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(232,185,106,0.18), transparent 60%)',
      linkIconColor: '#F5D58A',
    },
  },
  {
    id: 'obsidian-marble',
    name: 'Obsidian Marble',
    vibe: 'Black marble with gold vein',
    preview: '🪨',
    category: 'luxury',
    theme: {
      templateId: 'obsidian-marble',
      bg: 'linear-gradient(135deg, #0a0a0c 0%, #1a1a22 40%, #0a0a0c 100%)',
      text: '#FFFFFF', primary: '#D4AF37', secondary: '#9CA3AF', accent: '#FFFFFF',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 4, buttonShadow: 'none',
      cardBg: 'rgba(255,255,255,0.03)', cardBorder: 'rgba(212,175,55,0.35)',
      mutedText: 'rgba(255,255,255,0.55)', dividerColor: 'rgba(212,175,55,0.4)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%)',
      heroTint: 'linear-gradient(135deg, transparent 40%, rgba(212,175,55,0.12) 100%)',
      linkIconColor: '#D4AF37',
    },
  },
  {
    id: 'velvet-rouge',
    name: 'Velvet Rouge',
    vibe: 'Deep burgundy & rose gold',
    preview: '🍷',
    category: 'luxury',
    theme: {
      templateId: 'velvet-rouge',
      bg: 'linear-gradient(160deg, #1a0508 0%, #3d0a14 50%, #1a0508 100%)',
      text: '#FCE7E7', primary: '#E0A0A8', secondary: '#B76E79', accent: '#F5C6A5',
      font: 'serif', buttonStyle: 'glass', buttonRadius: 18, buttonShadow: 'soft',
      cardBg: 'rgba(224,160,168,0.08)', cardBorder: 'rgba(224,160,168,0.25)',
      mutedText: 'rgba(252,231,231,0.65)', dividerColor: 'rgba(224,160,168,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(20,4,8,0) 30%, rgba(20,4,8,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(224,160,168,0.18), transparent 60%)',
      linkIconColor: '#F5C6A5',
    },
  },
  // ===== ARTIST (NEW) =====
  {
    id: 'studio-monochrome',
    name: 'Studio Mono',
    vibe: 'Editorial grayscale',
    preview: '⚪',
    category: 'artist',
    theme: {
      templateId: 'studio-monochrome',
      bg: '#0e0e0e', text: '#F5F5F5', primary: '#FFFFFF', secondary: '#A3A3A3', accent: '#FFFFFF',
      font: 'mono', buttonStyle: 'outline', buttonRadius: 0, buttonShadow: 'none',
      cardBg: 'rgba(255,255,255,0.03)', cardBorder: 'rgba(255,255,255,0.15)',
      mutedText: 'rgba(245,245,245,0.55)', dividerColor: 'rgba(255,255,255,0.18)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%)',
      heroTint: 'transparent', linkIconColor: '#FFFFFF',
    },
  },
  {
    id: 'chrome-future',
    name: 'Chrome Future',
    vibe: 'Metallic silver & ice blue',
    preview: '🪞',
    category: 'artist',
    theme: {
      templateId: 'chrome-future',
      bg: 'linear-gradient(135deg, #0c1220 0%, #1e2a44 50%, #0c1220 100%)',
      text: '#F0F4FF', primary: '#C0D6F5', secondary: '#7B9BD8', accent: '#E8F0FF',
      font: 'display', buttonStyle: 'glass', buttonRadius: 9999, buttonShadow: 'strong',
      cardBg: 'rgba(192,214,245,0.08)', cardBorder: 'rgba(192,214,245,0.25)',
      mutedText: 'rgba(240,244,255,0.6)', dividerColor: 'rgba(192,214,245,0.25)',
      heroOverlay: 'linear-gradient(180deg, rgba(12,18,32,0) 30%, rgba(12,18,32,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(192,214,245,0.15), transparent)',
      linkIconColor: '#E8F0FF',
    },
  },
  {
    id: 'holographic',
    name: 'Holographic',
    vibe: 'Iridescent dream',
    preview: '🌈',
    category: 'artist',
    theme: {
      templateId: 'holographic',
      bg: 'linear-gradient(135deg, #0fb8c2 0%, #c346ff 50%, #ffae3b 100%)',
      text: '#FFFFFF', primary: '#FFFFFF', secondary: '#FCE7FF', accent: '#FFFAE3',
      font: 'display', buttonStyle: 'glass', buttonRadius: 20, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.14)', cardBorder: 'rgba(255,255,255,0.3)',
      mutedText: 'rgba(255,255,255,0.78)', dividerColor: 'rgba(255,255,255,0.35)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.4) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,255,255,0.18), transparent 60%)',
      linkIconColor: '#FFFFFF',
    },
  },
  {
    id: 'vinyl-noir',
    name: 'Vinyl Noir',
    vibe: 'Matte black, warm gold',
    preview: '🎶',
    category: 'artist',
    theme: {
      templateId: 'vinyl-noir',
      bg: 'radial-gradient(ellipse at top, #1a1410 0%, #050402 80%)',
      text: '#F5E6D3', primary: '#D4A574', secondary: '#8B5E34', accent: '#E8C99A',
      font: 'serif', buttonStyle: 'soft', buttonRadius: 10, buttonShadow: 'soft',
      cardBg: 'rgba(212,165,116,0.08)', cardBorder: 'rgba(212,165,116,0.25)',
      mutedText: 'rgba(245,230,211,0.6)', dividerColor: 'rgba(212,165,116,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(212,165,116,0.15), transparent)',
      linkIconColor: '#E8C99A',
    },
  },

  // ===== EXISTING =====
  {
    id: 'midnight-luxe', name: 'Midnight Luxe', vibe: 'Premium dark with gold accents', preview: '🌙', category: 'luxury',
    theme: { templateId: 'midnight-luxe', bg: 'linear-gradient(180deg,#0a0a0a 0%,#1a0a2e 100%)', text: '#FFFFFF', primary: '#F59E0B', secondary: '#8B5CF6', accent: '#FBBF24', font: 'display', buttonStyle: 'glass', buttonRadius: 16 },
  },
  {
    id: 'stage-lights', name: 'Stage Lights', vibe: 'Dramatic concert energy', preview: '🎭', category: 'bold',
    theme: { templateId: 'stage-lights', bg: 'radial-gradient(ellipse at top,#2A1510,#0A0A0A)', text: '#FFFFFF', primary: '#7C3AED', secondary: '#EC4899', accent: '#06B6D4', font: 'sans', buttonStyle: 'solid', buttonRadius: 12 },
  },
  {
    id: 'neon-underground', name: 'Neon Underground', vibe: 'Electric club vibes', preview: '⚡', category: 'bold',
    theme: { templateId: 'neon-underground', bg: '#000000', text: '#FFFFFF', primary: '#EC4899', secondary: '#06B6D4', accent: '#8B5CF6', font: 'mono', buttonStyle: 'outline', buttonRadius: 4 },
  },
  {
    id: 'golden-vinyl', name: 'Golden Vinyl', vibe: 'Retro luxury', preview: '💿', category: 'luxury',
    theme: { templateId: 'golden-vinyl', bg: 'linear-gradient(135deg,#1A1410,#3A2410)', text: '#FBBF24', primary: '#F59E0B', secondary: '#D97706', accent: '#FBBF24', font: 'serif', buttonStyle: 'soft', buttonRadius: 24 },
  },
  {
    id: 'clean-minimal', name: 'Clean Minimal', vibe: 'Modern typography', preview: '✨', category: 'minimal',
    theme: { templateId: 'clean-minimal', bg: '#FFFFFF', text: '#0A0A0A', primary: '#0A0A0A', secondary: '#6B7280', accent: '#E5E7EB', font: 'sans', buttonStyle: 'outline', buttonRadius: 8 },
  },
  {
    id: 'festival-poster', name: 'Festival Poster', vibe: 'Energetic & bold', preview: '🎪', category: 'bold',
    theme: { templateId: 'festival-poster', bg: 'linear-gradient(135deg,#EF4444,#F97316,#FBBF24)', text: '#FFFFFF', primary: '#1F2937', secondary: '#F97316', accent: '#FBBF24', font: 'display', buttonStyle: 'solid', buttonRadius: 0 },
  },
  {
    id: 'dark-acoustic', name: 'Dark Acoustic', vibe: 'Raw & intimate', preview: '🎸', category: 'artist',
    theme: { templateId: 'dark-acoustic', bg: 'linear-gradient(180deg,#1F1410,#0A0604)', text: '#F5E6D3', primary: '#92400E', secondary: '#B45309', accent: '#D97706', font: 'serif', buttonStyle: 'soft', buttonRadius: 8 },
  },
  {
    id: 'retro-cassette', name: 'Retro Cassette', vibe: '80s nostalgia', preview: '📼', category: 'artist',
    theme: { templateId: 'retro-cassette', bg: 'linear-gradient(135deg,#1F1F3D,#3D1F4E)', text: '#FFFFFF', primary: '#EC4899', secondary: '#06B6D4', accent: '#8B5CF6', font: 'mono', buttonStyle: 'glass', buttonRadius: 20 },
  },
  {
    id: 'cinematic', name: 'Cinematic', vibe: 'Film editorial', preview: '🎞️', category: 'artist',
    theme: { templateId: 'cinematic', bg: '#0D0D0D', text: '#FFFFFF', primary: '#7C3AED', secondary: '#EC4899', accent: '#FFFFFF', font: 'serif', buttonStyle: 'outline', buttonRadius: 0 },
  },
  {
    id: 'teal-tide', name: 'Teal Tide', vibe: 'Deep ocean gradient', preview: '🌊', category: 'bold',
    theme: { templateId: 'teal-tide', bg: 'linear-gradient(135deg,#0f2027 0%,#203a43 50%,#2c5364 100%)', text: '#FFFFFF', primary: '#2DD4BF', secondary: '#14B8A6', accent: '#5EEAD4', font: 'sans', buttonStyle: 'solid', buttonRadius: 999 },
  },
  {
    id: 'violet-bloom', name: 'Violet Bloom', vibe: 'Purple-pink with glass', preview: '💜', category: 'bold',
    theme: { templateId: 'violet-bloom', bg: 'linear-gradient(135deg,#7b2ff7 0%,#f107a3 100%)', text: '#FFFFFF', primary: '#FFFFFF', secondary: '#F107A3', accent: '#FFFFFF', font: 'display', buttonStyle: 'glass', buttonRadius: 16 },
  },
  {
    id: 'sunset-citrus', name: 'Sunset Citrus', vibe: 'Warm orange to yellow', preview: '🌅', category: 'bold',
    theme: { templateId: 'sunset-citrus', bg: 'linear-gradient(135deg,#f7971e 0%,#ffd200 100%)', text: '#1A1208', primary: '#1A1208', secondary: '#7C2D12', accent: '#FFFFFF', font: 'display', buttonStyle: 'solid', buttonRadius: 12 },
  },
  {
    id: 'red-alert', name: 'Red Alert', vibe: 'Black with red accents', preview: '🔴', category: 'bold',
    theme: { templateId: 'red-alert', bg: '#000000', text: '#FFFFFF', primary: '#FF2D2D', secondary: '#FF2D2D', accent: '#FF2D2D', font: 'mono', buttonStyle: 'outline', buttonRadius: 4 },
  },
  {
    id: 'pure-black', name: 'Pure Black', vibe: 'Brutal minimal', preview: '◼', category: 'minimal',
    theme: { templateId: 'pure-black', bg: '#000000', text: '#FFFFFF', primary: '#FFFFFF', secondary: '#888888', accent: '#FFFFFF', font: 'mono', buttonStyle: 'outline', buttonRadius: 0 },
  },
];

export const TEMPLATE_CATEGORIES: { id: TemplateCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'luxury', label: 'Luxury' },
  { id: 'artist', label: 'Artist' },
  { id: 'bold', label: 'Bold' },
  { id: 'minimal', label: 'Minimal' },
];

export const DEFAULT_THEME: LinkTreeTheme = { ...TEMPLATES[0].theme, buttonRadius: 9999 };

export function getTemplate(id?: string): Template {
  return TEMPLATES.find(t => t.id === id) ?? TEMPLATES[0];
}

export function fontFamily(font: LinkTreeTheme['font']) {
  switch (font) {
    case 'serif': return "'Playfair Display', Georgia, serif";
    case 'mono': return "'JetBrains Mono', 'SF Mono', monospace";
    case 'display': return "'Plus Jakarta Sans', system-ui, sans-serif";
    default: return "'Plus Jakarta Sans', system-ui, sans-serif";
  }
}

// =====================================================================
// ARTIST TEMPLATES — full bundles (palette + curated starter blocks).
// Used by the "Artist Templates" section in BuildTab > Theme. Apply
// replaces the page's blocks via the existing supabase client (no new
// table, no new RPC).
// =====================================================================

export type ArtistArchetype =
  | 'pop_star' | 'hip_hop' | 'indie_singer_songwriter' | 'electronic_producer'
  | 'rock_band' | 'rnb_vocalist' | 'latin' | 'country' | 'kpop' | 'dj_festival'
  | 'jazz_classic' | 'classical_virtuoso' | 'folk_storyteller' | 'blues_soul'
  | 'reggae_roots' | 'punk_zine' | 'metal_heavy' | 'trap_streets' | 'drill_uk'
  | 'afrobeats_vibes' | 'khiphop' | 'jpop_anime' | 'bollywood_glam'
  | 'mizrahi_mediterranean' | 'arabic_andalus' | 'brazilian_bossa'
  | 'country_rodeo' | 'edm_festival_2' | 'lofi_bedroom' | 'ambient_cinematic'
  | 'vaporwave_retro' | 'synthwave_neon' | 'y2k_glitter' | 'minimalist_mono'
  | 'maximalist_collage' | 'art_deco_luxe' | 'brutalist_raw' | 'glassmorphism_air'
  | 'magazine_editorial' | 'emerging_artist';

export type ArtistTemplateCategory =
  | 'all' | 'pop' | 'hip-hop' | 'indie' | 'electronic' | 'rock' | 'rnb' | 'world' | 'country' | 'aesthetic';

export const ARTIST_TEMPLATE_CATEGORIES: { id: ArtistTemplateCategory; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pop', label: 'Pop' },
  { id: 'hip-hop', label: 'Hip-Hop' },
  { id: 'indie', label: 'Indie' },
  { id: 'electronic', label: 'Electronic' },
  { id: 'rock', label: 'Rock' },
  { id: 'rnb', label: 'R&B' },
  { id: 'world', label: 'World' },
  { id: 'country', label: 'Country' },
  { id: 'aesthetic', label: 'Aesthetic' },
];

const ARCHETYPE_CATEGORY: Record<ArtistArchetype, Exclude<ArtistTemplateCategory, 'all'>> = {
  pop_star: 'pop', kpop: 'pop', jpop_anime: 'pop', bollywood_glam: 'pop', emerging_artist: 'pop',
  hip_hop: 'hip-hop', trap_streets: 'hip-hop', drill_uk: 'hip-hop', khiphop: 'hip-hop',
  indie_singer_songwriter: 'indie', folk_storyteller: 'indie', lofi_bedroom: 'indie',
  electronic_producer: 'electronic', dj_festival: 'electronic', edm_festival_2: 'electronic',
  ambient_cinematic: 'electronic', vaporwave_retro: 'electronic', synthwave_neon: 'electronic',
  rock_band: 'rock', punk_zine: 'rock', metal_heavy: 'rock',
  rnb_vocalist: 'rnb', blues_soul: 'rnb', jazz_classic: 'rnb',
  latin: 'world', brazilian_bossa: 'world', reggae_roots: 'world', afrobeats_vibes: 'world',
  mizrahi_mediterranean: 'world', arabic_andalus: 'world', classical_virtuoso: 'world',
  country: 'country', country_rodeo: 'country',
  y2k_glitter: 'aesthetic', minimalist_mono: 'aesthetic', maximalist_collage: 'aesthetic',
  art_deco_luxe: 'aesthetic', brutalist_raw: 'aesthetic', glassmorphism_air: 'aesthetic',
  magazine_editorial: 'aesthetic',
};

export function artistTemplateCategory(tpl: LinkTreeTemplate): Exclude<ArtistTemplateCategory, 'all'> {
  return ARCHETYPE_CATEGORY[tpl.archetype];
}

export interface LinkTreeTemplateBlock {
  type: BlockType;
  data: Record<string, any>;
  is_visible?: boolean;
}

export interface LinkTreeTemplate {
  id: string;
  name: string;
  archetype: ArtistArchetype;
  description: string;
  palette: LinkTreeTheme;
  blocks: LinkTreeTemplateBlock[];
}

const SOCIAL_DEFAULT = (items: { platform: string; url?: string }[]) => ({ items });

export const LINKTREE_TEMPLATES: LinkTreeTemplate[] = [
  {
    id: 'pop-star',
    name: 'Pop Star',
    archetype: 'pop_star',
    description: 'Bold gradient, big hero, single + tour CTA.',
    palette: {
      templateId: 'pop-star',
      bg: 'linear-gradient(135deg, #ff2d87 0%, #7b2ff7 50%, #2bd2ff 100%)',
      text: '#FFFFFF', primary: '#FFFFFF', secondary: '#FFD6F0', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'glass', buttonRadius: 9999, buttonShadow: 'strong',
      cardBg: 'rgba(255,255,255,0.12)', cardBorder: 'rgba(255,255,255,0.28)',
      mutedText: 'rgba(255,255,255,0.78)', dividerColor: 'rgba(255,255,255,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,45,135,0.35), transparent 60%)',
      linkIconColor: '#FFFFFF',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Pop Artist · New Single Out Now' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'youtube' }, { platform: 'spotify' }]) },
      { type: 'bio', data: { text: 'Chart-bound pop, glittering hooks, and a global stage.' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-06-12', city: 'Los Angeles', venue: 'The Forum', url: '' }, { date: '2026-06-20', city: 'New York', venue: 'MSG', url: '' }] } },
      
    ],
  },
  {
    id: 'hip-hop',
    name: 'Hip-Hop Artist',
    archetype: 'hip_hop',
    description: 'Dark glass, neon accents, merch + Spotify.',
    palette: {
      templateId: 'hip-hop',
      bg: 'radial-gradient(ellipse at top, #14142b 0%, #050507 70%)',
      text: '#F5F5F7', primary: '#00F0A8', secondary: '#7C3AED', accent: '#00F0A8',
      font: 'mono', buttonStyle: 'glass', buttonRadius: 8, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.04)', cardBorder: 'rgba(0,240,168,0.25)',
      mutedText: 'rgba(245,245,247,0.6)', dividerColor: 'rgba(0,240,168,0.25)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(0,240,168,0.18), transparent 60%)',
      linkIconColor: '#00F0A8',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Rapper · Producer · Founder' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'twitter' }, { platform: 'youtube' }]) },
      { type: 'bio', data: { text: 'Bars, beats, and business. Latest project out now.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'merch', data: { title: 'Official Merch', price: '$45', url: '', image: '' } },
    ],
  },
  {
    id: 'indie-singer-songwriter',
    name: 'Indie Singer-Songwriter',
    archetype: 'indie_singer_songwriter',
    description: 'Warm muted tones, intimate bio + tour dates.',
    palette: {
      templateId: 'indie-singer-songwriter',
      bg: 'linear-gradient(160deg, #f4ead5 0%, #e8d5b7 60%, #c9a96e 100%)',
      text: '#2a1a0d', primary: '#7b3f00', secondary: '#a85d2b', accent: '#5b2a08',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 4, buttonShadow: 'none',
      cardBg: 'rgba(255,255,255,0.4)', cardBorder: 'rgba(123,63,0,0.3)',
      mutedText: 'rgba(42,26,13,0.7)', dividerColor: 'rgba(123,63,0,0.25)',
      heroOverlay: 'linear-gradient(180deg, rgba(244,234,213,0) 30%, rgba(244,234,213,0.8) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#7b3f00',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Songs from the kitchen table' } },
      { type: 'bio', data: { text: 'I write quiet songs about loud feelings. New EP out this fall.' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'spotify' }, { platform: 'youtube' }]) },
      
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: '✍️ Read my journal', url: 'https://', icon: '📓' } },
    ],
  },
  {
    id: 'electronic-producer',
    name: 'Electronic Producer',
    archetype: 'electronic_producer',
    description: 'Cyberpunk grid, SoundCloud + upcoming sets.',
    palette: {
      templateId: 'electronic-producer',
      bg: 'linear-gradient(135deg, #0a0a1f 0%, #1a0a2e 50%, #0a0a1f 100%)',
      text: '#E0FFFF', primary: '#00FFFF', secondary: '#FF00FF', accent: '#FFFF00',
      font: 'mono', buttonStyle: 'outline', buttonRadius: 0, buttonShadow: 'strong',
      cardBg: 'rgba(0,255,255,0.06)', cardBorder: 'rgba(0,255,255,0.4)',
      mutedText: 'rgba(224,255,255,0.65)', dividerColor: 'rgba(0,255,255,0.35)',
      heroOverlay: 'linear-gradient(180deg, rgba(10,10,31,0.2) 0%, rgba(10,10,31,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,0,255,0.18), transparent 60%)',
      linkIconColor: '#00FFFF',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Producer · Live Sets · Synthesis' } },
      { type: 'soundcloud_embed', data: { url: '' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'soundcloud' }, { platform: 'spotify' }, { platform: 'instagram' }, { platform: 'youtube' }]) },
      { type: 'tour_dates', data: { dates: [{ date: '2026-07-04', city: 'Berlin', venue: 'Berghain', url: '' }, { date: '2026-08-15', city: 'Tokyo', venue: 'Contact', url: '' }] } },
      { type: 'link', data: { label: 'SoundCloud', url: 'https://', icon: '☁️' } },
      { type: 'link', data: { label: 'Bookings & remixes', url: 'mailto:bookings@', icon: '📩' } },
    ],
  },
  {
    id: 'rock-band',
    name: 'Rock Band',
    archetype: 'rock_band',
    description: 'High-contrast distressed, tour dates + merch.',
    palette: {
      templateId: 'rock-band',
      bg: 'linear-gradient(180deg, #0a0a0a 0%, #1a0505 100%)',
      text: '#FFFFFF', primary: '#FF1F1F', secondary: '#A30000', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'solid', buttonRadius: 0, buttonShadow: 'hard',
      cardBg: 'rgba(255,31,31,0.06)', cardBorder: 'rgba(255,31,31,0.4)',
      mutedText: 'rgba(255,255,255,0.6)', dividerColor: 'rgba(255,31,31,0.4)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,31,31,0.2), transparent 60%)',
      linkIconColor: '#FF1F1F',
    },
    blocks: [
      { type: 'header', data: { name: 'Band Name', tagline: 'Loud, Live, Always On Tour' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-05-10', city: 'London', venue: 'O2 Academy', url: '' }, { date: '2026-05-18', city: 'Manchester', venue: 'Apollo', url: '' }, { date: '2026-05-24', city: 'Glasgow', venue: 'Barrowlands', url: '' }] } },
      { type: 'merch', data: { title: 'Tour Hoodie', price: '$55', url: '', image: '' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'youtube' }, { platform: 'spotify' }, { platform: 'twitter' }]) },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'rnb-vocalist',
    name: 'R&B Vocalist',
    archetype: 'rnb_vocalist',
    description: 'Silk gradients, song embeds + handle row.',
    palette: {
      templateId: 'rnb-vocalist',
      bg: 'linear-gradient(160deg, #2a0a3a 0%, #5a1a4a 50%, #1a0520 100%)',
      text: '#FCE7F3', primary: '#E8B4D8', secondary: '#B76E9D', accent: '#F5C6E0',
      font: 'serif', buttonStyle: 'soft', buttonRadius: 24, buttonShadow: 'soft',
      cardBg: 'rgba(232,180,216,0.08)', cardBorder: 'rgba(232,180,216,0.25)',
      mutedText: 'rgba(252,231,243,0.65)', dividerColor: 'rgba(232,180,216,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(20,5,32,0) 30%, rgba(20,5,32,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(232,180,216,0.18), transparent 60%)',
      linkIconColor: '#F5C6E0',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'R&B · Soul · Late-Night Records' } },
      { type: 'bio', data: { text: 'Velvet vocals, slow grooves, Sunday-morning songs.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'handle', data: { platform: 'instagram', handle: '@yourname', url: '' } },
      { type: 'handle', data: { platform: 'tiktok', handle: '@yourname', url: '' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'latin',
    name: 'Latin Artist',
    archetype: 'latin',
    description: 'Vibrant warm gradient, multi-language bio + tour.',
    palette: {
      templateId: 'latin',
      bg: 'linear-gradient(135deg, #ff5e3a 0%, #ff2a68 50%, #ffb700 100%)',
      text: '#FFFFFF', primary: '#FFFFFF', secondary: '#FFE599', accent: '#1A0A00',
      font: 'display', buttonStyle: 'glass', buttonRadius: 16, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.14)', cardBorder: 'rgba(255,255,255,0.3)',
      mutedText: 'rgba(255,255,255,0.78)', dividerColor: 'rgba(255,255,255,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,183,0,0.25), transparent 60%)',
      linkIconColor: '#FFFFFF',
    },
    blocks: [
      { type: 'header', data: { name: 'Tu Nombre', tagline: 'Música Latina · World Tour' } },
      { type: 'bio', data: { text: 'EN: Latin pop with global rhythms. ES: Música latina con ritmos del mundo.' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'youtube' }, { platform: 'spotify' }]) },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-09-05', city: 'Madrid', venue: 'WiZink Center', url: '' }, { date: '2026-09-14', city: 'Mexico City', venue: 'Foro Sol', url: '' }, { date: '2026-09-22', city: 'Miami', venue: 'Kaseya Center', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'country',
    name: 'Country Artist',
    archetype: 'country',
    description: 'Americana textures, story bio + tour + merch.',
    palette: {
      templateId: 'country',
      bg: 'linear-gradient(180deg, #3d2817 0%, #1f1208 100%)',
      text: '#F5E6C8', primary: '#D4A24a', secondary: '#8B5E34', accent: '#E8C99A',
      font: 'serif', buttonStyle: 'soft', buttonRadius: 6, buttonShadow: 'soft',
      cardBg: 'rgba(212,162,74,0.08)', cardBorder: 'rgba(212,162,74,0.3)',
      mutedText: 'rgba(245,230,200,0.65)', dividerColor: 'rgba(212,162,74,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(212,162,74,0.18), transparent 60%)',
      linkIconColor: '#E8C99A',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Country · Story Songs · Honest Twang' } },
      { type: 'bio', data: { text: 'Born on a backroad, singing songs about the people who raised me.' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'spotify' }, { platform: 'youtube' }, { platform: 'tiktok' }]) },
      { type: 'tour_dates', data: { dates: [{ date: '2026-04-10', city: 'Nashville', venue: 'Ryman Auditorium', url: '' }, { date: '2026-04-22', city: 'Austin', venue: 'ACL Live', url: '' }] } },
      { type: 'merch', data: { title: 'Tour Tee', price: '$30', url: '', image: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'kpop',
    name: 'K-Pop Star',
    archetype: 'kpop',
    description: 'Pastel gradient + sparkle, multi-platform handles.',
    palette: {
      templateId: 'kpop',
      bg: 'linear-gradient(135deg, #ffd6f0 0%, #d6e4ff 50%, #d6fff5 100%)',
      text: '#2a0a4a', primary: '#7b2ff7', secondary: '#ff6ec7', accent: '#2bd2ff',
      font: 'display', buttonStyle: 'soft', buttonRadius: 9999, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.55)', cardBorder: 'rgba(123,47,247,0.2)',
      mutedText: 'rgba(42,10,74,0.65)', dividerColor: 'rgba(123,47,247,0.2)',
      heroOverlay: 'linear-gradient(180deg, rgba(255,214,240,0) 30%, rgba(255,214,240,0.6) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(123,47,247,0.12), transparent 60%)',
      linkIconColor: '#7b2ff7',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: '✨ K-Pop · Idol · Global Fandom ✨' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'youtube' }, { platform: 'twitter' }, { platform: 'spotify' }]) },
      { type: 'handle', data: { platform: 'weverse', handle: '@yourname', url: '' } },
      { type: 'handle', data: { platform: 'bubble', handle: '@yourname', url: '' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'image', data: { url: '', alt: 'Photo wall' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'dj-festival',
    name: 'DJ / Festival Headliner',
    archetype: 'dj_festival',
    description: 'Kinetic dark, gigs + SoundCloud + booking link.',
    palette: {
      templateId: 'dj-festival',
      bg: 'radial-gradient(ellipse at top, #1a0033 0%, #000010 70%)',
      text: '#FFFFFF', primary: '#FF00AA', secondary: '#00E5FF', accent: '#FFEA00',
      font: 'display', buttonStyle: 'glass', buttonRadius: 9999, buttonShadow: 'strong',
      cardBg: 'rgba(255,0,170,0.08)', cardBorder: 'rgba(255,0,170,0.35)',
      mutedText: 'rgba(255,255,255,0.65)', dividerColor: 'rgba(255,0,170,0.35)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 25%, rgba(0,0,0,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(0,229,255,0.18), transparent 60%)',
      linkIconColor: '#FF00AA',
    },
    blocks: [
      { type: 'header', data: { name: 'DJ NAME', tagline: 'DJ · Producer · Festival Headliner' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-06-28', city: 'Belgium', venue: 'Tomorrowland', url: '' }, { date: '2026-07-12', city: 'Las Vegas', venue: 'EDC', url: '' }, { date: '2026-08-09', city: 'Croatia', venue: 'Ultra Europe', url: '' }] } },
      { type: 'soundcloud_embed', data: { url: '' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'soundcloud' }, { platform: 'spotify' }, { platform: 'youtube' }]) },
      { type: 'link', data: { label: 'SoundCloud', url: 'https://', icon: '☁️' } },
      { type: 'link', data: { label: '📩 Booking enquiries', url: 'mailto:bookings@', icon: '✉️' } },
    ],
  },
  // ===== 30 PREMIUM ADDITIONS =====
  {
    id: 'jazz-classic', name: 'Jazz Standards', archetype: 'jazz_classic',
    description: 'Sepia & cream noir, vinyl mood.',
    palette: {
      templateId: 'jazz-classic',
      bg: 'radial-gradient(ellipse at top, #1c160e 0%, #0a0805 80%)',
      text: '#F2E4C9', primary: '#C9A96E', secondary: '#8B6F3D', accent: '#E8D5A8',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 2, buttonShadow: 'none',
      cardBg: 'rgba(201,169,110,0.06)', cardBorder: 'rgba(201,169,110,0.3)',
      mutedText: 'rgba(242,228,201,0.6)', dividerColor: 'rgba(201,169,110,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(201,169,110,0.18), transparent 60%)',
      linkIconColor: '#E8D5A8',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Jazz Standards · Live Quartet' } },
      { type: 'bio', data: { text: 'A modern voice for the great American songbook.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-05-12', city: 'New York', venue: 'Blue Note', url: '' }, { date: '2026-05-20', city: 'Paris', venue: 'Duc des Lombards', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'classical-virtuoso', name: 'Classical Virtuoso', archetype: 'classical_virtuoso',
    description: 'Marble & gold, concert hall elegance.',
    palette: {
      templateId: 'classical-virtuoso',
      bg: 'linear-gradient(160deg, #f4f1ea 0%, #e8e0cf 60%, #d4c8a8 100%)',
      text: '#1a1408', primary: '#7a5a1f', secondary: '#a8853f', accent: '#3d2f10',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 2, buttonShadow: 'none',
      cardBg: 'rgba(255,255,255,0.55)', cardBorder: 'rgba(122,90,31,0.3)',
      mutedText: 'rgba(26,20,8,0.65)', dividerColor: 'rgba(122,90,31,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(244,241,234,0) 35%, rgba(244,241,234,0.85) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#7a5a1f',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Concert Pianist · Recording Artist' } },
      { type: 'bio', data: { text: 'International soloist. Studied at the Royal Academy of Music.' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-10-04', city: 'Vienna', venue: 'Musikverein', url: '' }, { date: '2026-10-19', city: 'London', venue: 'Wigmore Hall', url: '' }] } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'folk-storyteller', name: 'Folk Storyteller', archetype: 'folk_storyteller',
    description: 'Kraft paper & ink, hand-drawn warmth.',
    palette: {
      templateId: 'folk-storyteller',
      bg: 'linear-gradient(180deg, #d9c19a 0%, #b89968 100%)',
      text: '#26180a', primary: '#3a2410', secondary: '#6b4a25', accent: '#1a0f06',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 0, buttonShadow: 'hard',
      cardBg: 'rgba(255,255,255,0.35)', cardBorder: 'rgba(38,24,10,0.35)',
      mutedText: 'rgba(38,24,10,0.7)', dividerColor: 'rgba(38,24,10,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(217,193,154,0) 35%, rgba(217,193,154,0.7) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#3a2410',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Folk · Songs & Stories' } },
      { type: 'bio', data: { text: 'Three chords, the truth, and a guitar that\u2019s been around the block.' } },
      { type: 'spotify_embed', data: { url: '' } },
      
      { type: 'link', data: { label: '📖 Read the liner notes', url: 'https://', icon: '✒️' } },
    ],
  },
  {
    id: 'blues-soul', name: 'Blues & Soul', archetype: 'blues_soul',
    description: 'Deep indigo + amber neon.',
    palette: {
      templateId: 'blues-soul',
      bg: 'linear-gradient(160deg, #0a0d2a 0%, #1a1f4a 50%, #0a0518 100%)',
      text: '#FFE9C2', primary: '#FFB347', secondary: '#5B6CFF', accent: '#FFD580',
      font: 'serif', buttonStyle: 'glass', buttonRadius: 12, buttonShadow: 'soft',
      cardBg: 'rgba(255,179,71,0.08)', cardBorder: 'rgba(255,179,71,0.3)',
      mutedText: 'rgba(255,233,194,0.65)', dividerColor: 'rgba(255,179,71,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(10,5,24,0) 30%, rgba(10,5,24,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,179,71,0.2), transparent 60%)',
      linkIconColor: '#FFB347',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Blues · Soul · Smoky Rooms' } },
      { type: 'bio', data: { text: 'Slow burns, long nights, songs that ache in the right places.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-06-14', city: 'Chicago', venue: 'Buddy Guy\u2019s Legends', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'reggae-roots', name: 'Reggae Roots', archetype: 'reggae_roots',
    description: 'Red, gold, green — sun-drenched.',
    palette: {
      templateId: 'reggae-roots',
      bg: 'linear-gradient(180deg, #1a3a1a 0%, #2e5d2e 50%, #0d1f0d 100%)',
      text: '#FFF8DC', primary: '#FFD700', secondary: '#E63946', accent: '#2E8B57',
      font: 'sans', buttonStyle: 'soft', buttonRadius: 16, buttonShadow: 'soft',
      cardBg: 'rgba(255,215,0,0.08)', cardBorder: 'rgba(255,215,0,0.3)',
      mutedText: 'rgba(255,248,220,0.7)', dividerColor: 'rgba(255,215,0,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(13,31,13,0) 30%, rgba(13,31,13,0.7) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,215,0,0.18), transparent 60%)',
      linkIconColor: '#FFD700',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Roots Reggae · One Love' } },
      { type: 'bio', data: { text: 'Conscious lyrics, irie vibrations, music for the people.' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'youtube' }, { platform: 'spotify' }]) },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-07-20', city: 'Kingston', venue: 'Reggae Sumfest', url: '' }] } },
    ],
  },
  {
    id: 'punk-zine', name: 'Punk Zine', archetype: 'punk_zine',
    description: 'High-contrast B&W, ransom-note typo.',
    palette: {
      templateId: 'punk-zine',
      bg: '#FFFFFF', text: '#000000', primary: '#000000', secondary: '#FF0000', accent: '#000000',
      font: 'mono', buttonStyle: 'solid', buttonRadius: 0, buttonShadow: 'hard',
      cardBg: '#FFFFFF', cardBorder: '#000000',
      mutedText: 'rgba(0,0,0,0.6)', dividerColor: '#000000',
      heroOverlay: 'linear-gradient(180deg, rgba(255,255,255,0) 35%, rgba(255,255,255,0.7) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#000000',
    },
    blocks: [
      { type: 'header', data: { name: 'BAND NAME', tagline: 'PUNK · DIY · NO SELLOUT' } },
      { type: 'bio', data: { text: 'Three chords. Loud amps. No apologies.' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-04-04', city: 'NYC', venue: 'Bowery Electric', url: '' }, { date: '2026-04-08', city: 'Philly', venue: 'First Unitarian Church', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'merch', data: { title: 'Tour Patch + Tee', price: '$20', url: '', image: '' } },
    ],
  },
  {
    id: 'metal-heavy', name: 'Heavy Metal', archetype: 'metal_heavy',
    description: 'Charcoal + blood red, gothic.',
    palette: {
      templateId: 'metal-heavy',
      bg: 'radial-gradient(ellipse at top, #1a0505 0%, #000000 80%)',
      text: '#E5E5E5', primary: '#B30000', secondary: '#660000', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'outline', buttonRadius: 0, buttonShadow: 'hard',
      cardBg: 'rgba(179,0,0,0.08)', cardBorder: 'rgba(179,0,0,0.5)',
      mutedText: 'rgba(229,229,229,0.6)', dividerColor: 'rgba(179,0,0,0.5)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.9) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(179,0,0,0.25), transparent 60%)',
      linkIconColor: '#B30000',
    },
    blocks: [
      { type: 'header', data: { name: 'BAND NAME', tagline: 'Metal · Loud · Unholy' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-09-12', city: 'Wacken', venue: 'Wacken Open Air', url: '' }, { date: '2026-09-26', city: 'Donington', venue: 'Download Festival', url: '' }] } },
      { type: 'merch', data: { title: 'Album Vinyl', price: '$35', url: '', image: '' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'trap-streets', name: 'Trap', archetype: 'trap_streets',
    description: 'Matte black + holographic foil.',
    palette: {
      templateId: 'trap-streets',
      bg: '#0a0a0a', text: '#FFFFFF', primary: '#C0C0FF', secondary: '#FF4FD8', accent: '#7CFFD8',
      font: 'mono', buttonStyle: 'glass', buttonRadius: 6, buttonShadow: 'strong',
      cardBg: 'rgba(255,255,255,0.04)', cardBorder: 'rgba(192,192,255,0.3)',
      mutedText: 'rgba(255,255,255,0.6)', dividerColor: 'rgba(192,192,255,0.25)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,79,216,0.18), rgba(124,255,216,0.12) 80%)',
      linkIconColor: '#C0C0FF',
    },
    blocks: [
      { type: 'header', data: { name: 'YOUR NAME', tagline: 'Trap · 808s · Late Nights' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'twitter' }]) },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'merch', data: { title: 'Foil Tee', price: '$50', url: '', image: '' } },
    ],
  },
  {
    id: 'drill-uk', name: 'UK Drill', archetype: 'drill_uk',
    description: 'Cold concrete + neon green.',
    palette: {
      templateId: 'drill-uk',
      bg: 'linear-gradient(180deg, #1a1c1e 0%, #0a0b0c 100%)',
      text: '#E8F8E8', primary: '#39FF14', secondary: '#7A7E82', accent: '#FFFFFF',
      font: 'mono', buttonStyle: 'outline', buttonRadius: 2, buttonShadow: 'none',
      cardBg: 'rgba(57,255,20,0.05)', cardBorder: 'rgba(57,255,20,0.35)',
      mutedText: 'rgba(232,248,232,0.55)', dividerColor: 'rgba(57,255,20,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.85) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(57,255,20,0.18), transparent 60%)',
      linkIconColor: '#39FF14',
    },
    blocks: [
      { type: 'header', data: { name: 'YOUR NAME', tagline: 'UK Drill · Real Talk' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'youtube' }]) },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'afrobeats-vibes', name: 'Afrobeats', archetype: 'afrobeats_vibes',
    description: 'Sunset gradient + tribal motifs.',
    palette: {
      templateId: 'afrobeats-vibes',
      bg: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 40%, #c1121f 100%)',
      text: '#FFFFFF', primary: '#FFFFFF', secondary: '#FFE066', accent: '#1A0500',
      font: 'display', buttonStyle: 'glass', buttonRadius: 9999, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.14)', cardBorder: 'rgba(255,255,255,0.3)',
      mutedText: 'rgba(255,255,255,0.78)', dividerColor: 'rgba(255,255,255,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,224,102,0.25), transparent 60%)',
      linkIconColor: '#FFFFFF',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Afrobeats · Naija · Global Wave' } },
      { type: 'bio', data: { text: 'Lagos to London to LA. New single dey hot.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-08-08', city: 'Lagos', venue: 'Eko Hotel', url: '' }, { date: '2026-08-22', city: 'London', venue: 'O2 Arena', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'khiphop', name: 'K-Hip-Hop', archetype: 'khiphop',
    description: 'Gradient lavender + chrome.',
    palette: {
      templateId: 'khiphop',
      bg: 'linear-gradient(160deg, #1a0a2e 0%, #4a1a6a 50%, #0a0518 100%)',
      text: '#F0E8FF', primary: '#C0B8FF', secondary: '#7A6AC8', accent: '#FFFFFF',
      font: 'mono', buttonStyle: 'glass', buttonRadius: 4, buttonShadow: 'strong',
      cardBg: 'rgba(192,184,255,0.08)', cardBorder: 'rgba(192,184,255,0.3)',
      mutedText: 'rgba(240,232,255,0.65)', dividerColor: 'rgba(192,184,255,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(10,5,24,0) 30%, rgba(10,5,24,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(192,184,255,0.18), transparent 60%)',
      linkIconColor: '#C0B8FF',
    },
    blocks: [
      { type: 'header', data: { name: '당신의 이름', tagline: 'K-Hip-Hop · Seoul · Bars' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'youtube' }]) },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'jpop-anime', name: 'J-Pop / Anime', archetype: 'jpop_anime',
    description: 'Pastel kawaii + sparkle.',
    palette: {
      templateId: 'jpop-anime',
      bg: 'linear-gradient(135deg, #ffe0f0 0%, #e0f0ff 50%, #fff0e0 100%)',
      text: '#3a1a4a', primary: '#ff6ec7', secondary: '#7ac8ff', accent: '#ffd700',
      font: 'display', buttonStyle: 'soft', buttonRadius: 9999, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.6)', cardBorder: 'rgba(255,110,199,0.3)',
      mutedText: 'rgba(58,26,74,0.65)', dividerColor: 'rgba(255,110,199,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(255,224,240,0) 30%, rgba(255,224,240,0.6) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,110,199,0.15), transparent 60%)',
      linkIconColor: '#ff6ec7',
    },
    blocks: [
      { type: 'header', data: { name: 'あなたの名前', tagline: '✨ J-Pop · Idol · Kawaii ✨' } },
      { type: 'bio', data: { text: 'Sparkle pop from Tokyo. New single out now!' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'youtube' }, { platform: 'twitter' }]) },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'bollywood-glam', name: 'Bollywood Glam', archetype: 'bollywood_glam',
    description: 'Saffron + magenta + gold lattice.',
    palette: {
      templateId: 'bollywood-glam',
      bg: 'linear-gradient(135deg, #ff6f00 0%, #c2185b 50%, #6a1b9a 100%)',
      text: '#FFF8E1', primary: '#FFD700', secondary: '#FFFFFF', accent: '#FF6F00',
      font: 'display', buttonStyle: 'glass', buttonRadius: 16, buttonShadow: 'strong',
      cardBg: 'rgba(255,215,0,0.12)', cardBorder: 'rgba(255,215,0,0.4)',
      mutedText: 'rgba(255,248,225,0.78)', dividerColor: 'rgba(255,215,0,0.35)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,215,0,0.22), transparent 60%)',
      linkIconColor: '#FFD700',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Bollywood · Playback · Cinema' } },
      { type: 'bio', data: { text: 'Voice of the silver screen. Latest film soundtrack out now.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'mizrahi-mediterranean', name: 'Mizrahi / Mediterranean', archetype: 'mizrahi_mediterranean',
    description: 'Sand + turquoise sea.',
    palette: {
      templateId: 'mizrahi-mediterranean',
      bg: 'linear-gradient(180deg, #e8d8b0 0%, #4ab8c4 70%, #1a5a78 100%)',
      text: '#0a1a2a', primary: '#0a4a78', secondary: '#c89858', accent: '#FFFFFF',
      font: 'serif', buttonStyle: 'soft', buttonRadius: 12, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.4)', cardBorder: 'rgba(10,74,120,0.3)',
      mutedText: 'rgba(10,26,42,0.7)', dividerColor: 'rgba(10,74,120,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(232,216,176,0) 30%, rgba(26,90,120,0.5) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(74,184,196,0.2), transparent 60%)',
      linkIconColor: '#0a4a78',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Mizrahi · Mediterranean Soul' } },
      { type: 'bio', data: { text: 'Songs from the sea, the sun, and the old neighborhood.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-07-04', city: 'Tel Aviv', venue: 'Bloomfield', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'arabic-andalus', name: 'Arabic Andalusian', archetype: 'arabic_andalus',
    description: 'Deep emerald + ornament.',
    palette: {
      templateId: 'arabic-andalus',
      bg: 'radial-gradient(ellipse at top, #0a3a2a 0%, #021a10 80%)',
      text: '#F4E5C2', primary: '#D4AF37', secondary: '#1F8A5E', accent: '#FFFFFF',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 9999, buttonShadow: 'soft',
      cardBg: 'rgba(212,175,55,0.06)', cardBorder: 'rgba(212,175,55,0.35)',
      mutedText: 'rgba(244,229,194,0.65)', dividerColor: 'rgba(212,175,55,0.35)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(212,175,55,0.18), transparent 60%)',
      linkIconColor: '#D4AF37',
    },
    blocks: [
      { type: 'header', data: { name: 'اسمك', tagline: 'Arabic · Andalusian · Tarab' } },
      { type: 'bio', data: { text: 'Classical maqam meets modern sound. New album out now.' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'brazilian-bossa', name: 'Brazilian Bossa', archetype: 'brazilian_bossa',
    description: 'Tropical aqua + warm coral.',
    palette: {
      templateId: 'brazilian-bossa',
      bg: 'linear-gradient(135deg, #2cc7c0 0%, #f5b04a 50%, #ff7e5f 100%)',
      text: '#FFFFFF', primary: '#FFFFFF', secondary: '#1a4a4a', accent: '#FFE9C2',
      font: 'serif', buttonStyle: 'glass', buttonRadius: 16, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.16)', cardBorder: 'rgba(255,255,255,0.3)',
      mutedText: 'rgba(255,255,255,0.8)', dividerColor: 'rgba(255,255,255,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.45) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(44,199,192,0.2), transparent 60%)',
      linkIconColor: '#FFFFFF',
    },
    blocks: [
      { type: 'header', data: { name: 'Seu Nome', tagline: 'Bossa Nova · MPB · Saudade' } },
      { type: 'bio', data: { text: 'Songs from the beach, sung soft on a nylon string.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-11-08', city: 'Rio', venue: 'Vivo Rio', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'country-rodeo', name: 'Country Rodeo', archetype: 'country_rodeo',
    description: 'Denim + leather + sunset.',
    palette: {
      templateId: 'country-rodeo',
      bg: 'linear-gradient(180deg, #2a3a5a 0%, #6a3a2a 50%, #c25a2a 100%)',
      text: '#FFE9C2', primary: '#FFD580', secondary: '#8B4513', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'solid', buttonRadius: 6, buttonShadow: 'hard',
      cardBg: 'rgba(255,213,128,0.08)', cardBorder: 'rgba(255,213,128,0.35)',
      mutedText: 'rgba(255,233,194,0.7)', dividerColor: 'rgba(255,213,128,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.6) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,213,128,0.2), transparent 60%)',
      linkIconColor: '#FFD580',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Country · Rodeo · Boots & Trucks' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-06-04', city: 'Houston', venue: 'NRG Stadium', url: '' }, { date: '2026-06-18', city: 'Calgary', venue: 'Stampede', url: '' }] } },
      { type: 'merch', data: { title: 'Trucker Hat', price: '$28', url: '', image: '' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'edm-festival-2', name: 'EDM Mainstage', archetype: 'edm_festival_2',
    description: 'Electric purple + strobe white.',
    palette: {
      templateId: 'edm-festival-2',
      bg: 'radial-gradient(ellipse at top, #4a0a8a 0%, #0a0028 80%)',
      text: '#FFFFFF', primary: '#B388FF', secondary: '#FFFFFF', accent: '#00E5FF',
      font: 'display', buttonStyle: 'glass', buttonRadius: 9999, buttonShadow: 'strong',
      cardBg: 'rgba(179,136,255,0.08)', cardBorder: 'rgba(179,136,255,0.35)',
      mutedText: 'rgba(255,255,255,0.65)', dividerColor: 'rgba(179,136,255,0.35)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 25%, rgba(0,0,0,0.8) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(179,136,255,0.22), transparent 60%)',
      linkIconColor: '#B388FF',
    },
    blocks: [
      { type: 'header', data: { name: 'DJ NAME', tagline: 'EDM · Mainstage · Anthems' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-07-25', city: 'Boom', venue: 'Tomorrowland', url: '' }, { date: '2026-08-15', city: 'Miami', venue: 'Ultra', url: '' }] } },
      { type: 'soundcloud_embed', data: { url: '' } },
      { type: 'link', data: { label: 'SoundCloud', url: 'https://', icon: '☁️' } },
      { type: 'link', data: { label: '📩 Booking', url: 'mailto:bookings@', icon: '✉️' } },
    ],
  },
  {
    id: 'lofi-bedroom', name: 'Lo-Fi Bedroom', archetype: 'lofi_bedroom',
    description: 'Muted plum + warm grain.',
    palette: {
      templateId: 'lofi-bedroom',
      bg: 'linear-gradient(160deg, #2a1a2e 0%, #3a2638 60%, #1a0e1c 100%)',
      text: '#F0E0D8', primary: '#D8A8C8', secondary: '#8a6878', accent: '#F5C9B0',
      font: 'serif', buttonStyle: 'soft', buttonRadius: 18, buttonShadow: 'soft',
      cardBg: 'rgba(216,168,200,0.07)', cardBorder: 'rgba(216,168,200,0.25)',
      mutedText: 'rgba(240,224,216,0.65)', dividerColor: 'rgba(216,168,200,0.25)',
      heroOverlay: 'linear-gradient(180deg, rgba(26,14,28,0) 30%, rgba(26,14,28,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(216,168,200,0.15), transparent 60%)',
      linkIconColor: '#D8A8C8',
    },
    blocks: [
      { type: 'header', data: { name: 'your name', tagline: 'lo-fi · beats to chill to' } },
      { type: 'bio', data: { text: 'making beats from a bedroom in the rain.' } },
      { type: 'soundcloud_embed', data: { url: '' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'SoundCloud', url: 'https://', icon: '☁️' } },
    ],
  },
  {
    id: 'ambient-cinematic', name: 'Ambient Cinematic', archetype: 'ambient_cinematic',
    description: 'Slate blue + cloud.',
    palette: {
      templateId: 'ambient-cinematic',
      bg: 'linear-gradient(180deg, #1a2838 0%, #4a5a6a 60%, #c8d2dc 100%)',
      text: '#0a1828', primary: '#1a3a5a', secondary: '#5a7a98', accent: '#FFFFFF',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 4, buttonShadow: 'none',
      cardBg: 'rgba(255,255,255,0.35)', cardBorder: 'rgba(26,58,90,0.3)',
      mutedText: 'rgba(10,24,40,0.7)', dividerColor: 'rgba(26,58,90,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(26,40,56,0) 30%, rgba(26,40,56,0.5) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#1a3a5a',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Ambient · Score · Texture' } },
      { type: 'bio', data: { text: 'Soundtracks for stillness. New score out now.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'youtube_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'YouTube', url: 'https://', icon: '▶️' } },
    ],
  },
  {
    id: 'vaporwave-retro', name: 'Vaporwave', archetype: 'vaporwave_retro',
    description: 'Magenta/teal grid + chrome.',
    palette: {
      templateId: 'vaporwave-retro',
      bg: 'linear-gradient(180deg, #ff71ce 0%, #b967ff 40%, #01cdfe 80%, #05ffa1 100%)',
      text: '#FFFFFF', primary: '#FFFFFF', secondary: '#01cdfe', accent: '#ff71ce',
      font: 'mono', buttonStyle: 'glass', buttonRadius: 0, buttonShadow: 'strong',
      cardBg: 'rgba(255,255,255,0.16)', cardBorder: 'rgba(255,255,255,0.35)',
      mutedText: 'rgba(255,255,255,0.78)', dividerColor: 'rgba(255,255,255,0.35)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.4) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,113,206,0.2), transparent 60%)',
      linkIconColor: '#FFFFFF',
    },
    blocks: [
      { type: 'header', data: { name: 'A E S T H E T I C', tagline: 'Vaporwave · Retro · Mall' } },
      { type: 'bio', data: { text: 'シティポップ. plastic palms. forever 1995.' } },
      { type: 'soundcloud_embed', data: { url: '' } },
      { type: 'image', data: { url: '', alt: 'Marble bust' } },
      { type: 'link', data: { label: 'SoundCloud', url: 'https://', icon: '☁️' } },
    ],
  },
  {
    id: 'synthwave-neon', name: 'Synthwave', archetype: 'synthwave_neon',
    description: 'Sunset gradient + neon grid.',
    palette: {
      templateId: 'synthwave-neon',
      bg: 'linear-gradient(180deg, #0a0028 0%, #4a1a6a 40%, #ff2a68 80%, #ffb84d 100%)',
      text: '#FFFFFF', primary: '#ff2a68', secondary: '#00f0ff', accent: '#ffb84d',
      font: 'mono', buttonStyle: 'outline', buttonRadius: 0, buttonShadow: 'strong',
      cardBg: 'rgba(255,42,104,0.08)', cardBorder: 'rgba(0,240,255,0.4)',
      mutedText: 'rgba(255,255,255,0.7)', dividerColor: 'rgba(0,240,255,0.4)',
      heroOverlay: 'linear-gradient(180deg, rgba(10,0,40,0) 30%, rgba(10,0,40,0.55) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(0,240,255,0.18), transparent 60%)',
      linkIconColor: '#00f0ff',
    },
    blocks: [
      { type: 'header', data: { name: 'YOUR NAME', tagline: 'Synthwave · Outrun · 1985' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'soundcloud_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'SoundCloud', url: 'https://', icon: '☁️' } },
      { type: 'merch', data: { title: 'Cassette + Poster', price: '$25', url: '', image: '' } },
    ],
  },
  {
    id: 'y2k-glitter', name: 'Y2K Glitter', archetype: 'y2k_glitter',
    description: 'Frosted lavender + glitter.',
    palette: {
      templateId: 'y2k-glitter',
      bg: 'linear-gradient(135deg, #e0c8ff 0%, #c8e0ff 50%, #ffe0f0 100%)',
      text: '#3a1a5a', primary: '#9b6cff', secondary: '#ff6ec7', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'glass', buttonRadius: 9999, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.5)', cardBorder: 'rgba(155,108,255,0.3)',
      mutedText: 'rgba(58,26,90,0.65)', dividerColor: 'rgba(155,108,255,0.3)',
      heroOverlay: 'linear-gradient(180deg, rgba(224,200,255,0) 30%, rgba(224,200,255,0.5) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,110,199,0.18), transparent 60%)',
      linkIconColor: '#9b6cff',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: '✨ Y2K Pop · Sparkle · Cyber Princess ✨' } },
      { type: 'bio', data: { text: 'frosted gloss, butterfly clips, and a hit single.' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }]) },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'minimalist-mono', name: 'Minimalist Mono', archetype: 'minimalist_mono',
    description: 'True black + razor-thin type.',
    palette: {
      templateId: 'minimalist-mono',
      bg: '#000000', text: '#FFFFFF', primary: '#FFFFFF', secondary: '#888888', accent: '#FFFFFF',
      font: 'sans', buttonStyle: 'outline', buttonRadius: 0, buttonShadow: 'none',
      cardBg: 'transparent', cardBorder: 'rgba(255,255,255,0.15)',
      mutedText: 'rgba(255,255,255,0.5)', dividerColor: 'rgba(255,255,255,0.15)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#FFFFFF',
    },
    blocks: [
      { type: 'header', data: { name: 'name', tagline: '—' } },
      { type: 'bio', data: { text: 'work · listen · contact' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
      { type: 'link', data: { label: 'contact', url: 'mailto:', icon: '' } },
    ],
  },
  {
    id: 'maximalist-collage', name: 'Maximalist Collage', archetype: 'maximalist_collage',
    description: 'Color-block + sticker overlays.',
    palette: {
      templateId: 'maximalist-collage',
      bg: 'linear-gradient(135deg, #ffea00 0%, #ff3ea5 35%, #00e5ff 70%, #00ff85 100%)',
      text: '#0a0a0a', primary: '#0a0a0a', secondary: '#ff3ea5', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'solid', buttonRadius: 12, buttonShadow: 'hard',
      cardBg: 'rgba(255,255,255,0.7)', cardBorder: '#0a0a0a',
      mutedText: 'rgba(10,10,10,0.7)', dividerColor: '#0a0a0a',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.3) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,62,165,0.2), transparent 60%)',
      linkIconColor: '#0a0a0a',
    },
    blocks: [
      { type: 'header', data: { name: 'YOUR NAME!!', tagline: 'Pop · Punk · Color · Chaos' } },
      { type: 'bio', data: { text: 'MORE IS MORE. New record. New look. New everything.' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'youtube' }]) },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'merch', data: { title: 'Sticker Pack', price: '$12', url: '', image: '' } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'art-deco-luxe', name: 'Art Deco Luxe', archetype: 'art_deco_luxe',
    description: 'Black + gold geometric fans.',
    palette: {
      templateId: 'art-deco-luxe',
      bg: 'linear-gradient(135deg, #0a0a0a 0%, #1a1408 50%, #0a0a0a 100%)',
      text: '#F5E6B8', primary: '#D4AF37', secondary: '#8B6914', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'outline', buttonRadius: 2, buttonShadow: 'soft',
      cardBg: 'rgba(212,175,55,0.06)', cardBorder: 'rgba(212,175,55,0.4)',
      mutedText: 'rgba(245,230,184,0.65)', dividerColor: 'rgba(212,175,55,0.4)',
      heroOverlay: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.85) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(212,175,55,0.18), transparent 60%)',
      linkIconColor: '#D4AF37',
    },
    blocks: [
      { type: 'header', data: { name: 'YOUR NAME', tagline: 'A NEW ALBUM · 2026' } },
      { type: 'divider', data: {} },
      { type: 'bio', data: { text: 'Modern songs in the grand tradition.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'tour_dates', data: { dates: [{ date: '2026-12-31', city: 'Paris', venue: 'Olympia', url: '' }] } },
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'brutalist-raw', name: 'Brutalist Raw', archetype: 'brutalist_raw',
    description: 'Concrete grey + harsh borders.',
    palette: {
      templateId: 'brutalist-raw',
      bg: '#9a9a98', text: '#0a0a0a', primary: '#0a0a0a', secondary: '#ff4400', accent: '#FFFFFF',
      font: 'mono', buttonStyle: 'solid', buttonRadius: 0, buttonShadow: 'hard',
      cardBg: '#c8c8c4', cardBorder: '#0a0a0a',
      mutedText: 'rgba(10,10,10,0.65)', dividerColor: '#0a0a0a',
      heroOverlay: 'linear-gradient(180deg, rgba(154,154,152,0) 35%, rgba(154,154,152,0.6) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#0a0a0a',
    },
    blocks: [
      { type: 'header', data: { name: 'NAME', tagline: 'NOISE / DRONE / EXPERIMENTAL' } },
      { type: 'bio', data: { text: 'No website. No image. Only the work.' } },
      { type: 'soundcloud_embed', data: { url: '' } },
      { type: 'link', data: { label: 'SoundCloud', url: 'https://', icon: '☁️' } },
      { type: 'link', data: { label: 'CONTACT', url: 'mailto:', icon: '' } },
    ],
  },
  {
    id: 'glassmorphism-air', name: 'Glassmorphism', archetype: 'glassmorphism_air',
    description: 'Frosted glass cards + soft gradient.',
    palette: {
      templateId: 'glassmorphism-air',
      bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 50%, #fbc2eb 100%)',
      text: '#1a1a2e', primary: '#5a6ac8', secondary: '#c878a8', accent: '#FFFFFF',
      font: 'display', buttonStyle: 'glass', buttonRadius: 18, buttonShadow: 'soft',
      cardBg: 'rgba(255,255,255,0.35)', cardBorder: 'rgba(255,255,255,0.5)',
      mutedText: 'rgba(26,26,46,0.65)', dividerColor: 'rgba(255,255,255,0.5)',
      heroOverlay: 'linear-gradient(180deg, rgba(161,196,253,0) 30%, rgba(161,196,253,0.5) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%)',
      linkIconColor: '#5a6ac8',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'Soft Pop · Air · Light' } },
      { type: 'bio', data: { text: 'Music to breathe to.' } },
      { type: 'spotify_embed', data: { url: '' } },
      
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
  {
    id: 'magazine-editorial', name: 'Magazine Editorial', archetype: 'magazine_editorial',
    description: 'Serif + photo-led layout.',
    palette: {
      templateId: 'magazine-editorial',
      bg: '#f7f3ee', text: '#0a0a0a', primary: '#0a0a0a', secondary: '#a83232', accent: '#0a0a0a',
      font: 'serif', buttonStyle: 'outline', buttonRadius: 0, buttonShadow: 'none',
      cardBg: '#FFFFFF', cardBorder: 'rgba(10,10,10,0.15)',
      mutedText: 'rgba(10,10,10,0.6)', dividerColor: 'rgba(10,10,10,0.2)',
      heroOverlay: 'linear-gradient(180deg, rgba(247,243,238,0) 35%, rgba(247,243,238,0.7) 100%)',
      heroTint: 'transparent',
      linkIconColor: '#0a0a0a',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'A profile in sound.' } },
      { type: 'image', data: { url: '', alt: 'Editorial portrait' } },
      { type: 'bio', data: { text: 'Featured in: Pitchfork, NME, The Fader, Crack.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'link', data: { label: 'Read the full feature →', url: 'https://', icon: '' } },
    ],
  },
  {
    id: 'emerging-artist', name: 'Emerging Artist', archetype: 'emerging_artist',
    description: 'Clean optimistic + "now playing" focus.',
    palette: {
      templateId: 'emerging-artist',
      bg: 'linear-gradient(180deg, #0e1424 0%, #142036 100%)',
      text: '#F5F8FF', primary: '#5EEAD4', secondary: '#8B5CF6', accent: '#FBBF24',
      font: 'display', buttonStyle: 'solid', buttonRadius: 12, buttonShadow: 'soft',
      cardBg: 'rgba(94,234,212,0.06)', cardBorder: 'rgba(94,234,212,0.25)',
      mutedText: 'rgba(245,248,255,0.65)', dividerColor: 'rgba(94,234,212,0.25)',
      heroOverlay: 'linear-gradient(180deg, rgba(14,20,36,0) 30%, rgba(14,20,36,0.75) 100%)',
      heroTint: 'linear-gradient(135deg, rgba(94,234,212,0.18), transparent 60%)',
      linkIconColor: '#5EEAD4',
    },
    blocks: [
      { type: 'header', data: { name: 'Your Name', tagline: 'New artist · First single out now' } },
      { type: 'bio', data: { text: 'Just dropped my debut. Thanks for being here early.' } },
      { type: 'spotify_embed', data: { url: '' } },
      { type: 'social_row', data: SOCIAL_DEFAULT([{ platform: 'instagram' }, { platform: 'tiktok' }, { platform: 'spotify' }]) },
      
      { type: 'link', data: { label: 'Spotify', url: 'https://', icon: '🟢' } },
    ],
  },
];

export function getArtistTemplate(id: string): LinkTreeTemplate | undefined {
  return LINKTREE_TEMPLATES.find(t => t.id === id);
}
